import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import LeaveComplianceService, {
  type ComplianceProfileDto,
  type SaveComplianceProfileDto,
  type StatutoryRuleDto,
  type SaveStatutoryRuleDto,
  type LegalSourceDto,
  type SaveLegalSourceDto,
  type RuleVersionHistoryDto,
} from "@/services/api/leaveCompliance";

interface LeaveComplianceState {
  profiles: ComplianceProfileDto[];
  statutoryRules: StatutoryRuleDto[];
  legalSources: LegalSourceDto[];
  versionHistory: RuleVersionHistoryDto[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

const initialState: LeaveComplianceState = {
  profiles: [],
  statutoryRules: [],
  legalSources: [],
  versionHistory: [],
  isLoading: false,
  isSaving: false,
  error: null,
};

// Profiles Thunks
export const fetchComplianceProfiles = createAsyncThunk(
  "leaveCompliance/fetchProfiles",
  async (_, { rejectWithValue }) => {
    try {
      const res = await LeaveComplianceService.getComplianceProfiles();
      return res.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to fetch compliance profiles");
    }
  }
);

export const createComplianceProfile = createAsyncThunk(
  "leaveCompliance/createProfile",
  async (payload: SaveComplianceProfileDto, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveComplianceService.createComplianceProfile(payload);
      dispatch(fetchComplianceProfiles());
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to create compliance profile");
    }
  }
);

export const updateComplianceProfile = createAsyncThunk(
  "leaveCompliance/updateProfile",
  async (payload: SaveComplianceProfileDto, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveComplianceService.updateComplianceProfile(payload);
      dispatch(fetchComplianceProfiles());
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update compliance profile");
    }
  }
);

export const toggleComplianceProfileStatus = createAsyncThunk(
  "leaveCompliance/toggleProfileStatus",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveComplianceService.toggleComplianceProfileStatus(id);
      dispatch(fetchComplianceProfiles());
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to toggle profile status");
    }
  }
);

// Statutory Rules Thunks
export const fetchStatutoryRules = createAsyncThunk(
  "leaveCompliance/fetchStatutoryRules",
  async (profileId: number | undefined, { rejectWithValue }) => {
    try {
      const res = await LeaveComplianceService.getStatutoryRules(profileId);
      return res.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to fetch statutory rules");
    }
  }
);

export const createStatutoryRule = createAsyncThunk(
  "leaveCompliance/createStatutoryRule",
  async (payload: SaveStatutoryRuleDto, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveComplianceService.createStatutoryRule(payload);
      dispatch(fetchStatutoryRules());
      dispatch(fetchRuleVersionHistory());
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to create statutory rule");
    }
  }
);

export const updateStatutoryRule = createAsyncThunk(
  "leaveCompliance/updateStatutoryRule",
  async (payload: SaveStatutoryRuleDto, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveComplianceService.updateStatutoryRule(payload);
      dispatch(fetchStatutoryRules());
      dispatch(fetchRuleVersionHistory());
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update statutory rule");
    }
  }
);

export const deleteStatutoryRule = createAsyncThunk(
  "leaveCompliance/deleteStatutoryRule",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveComplianceService.deleteStatutoryRule(id);
      dispatch(fetchStatutoryRules());
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete statutory rule");
    }
  }
);

// Legal Sources Thunks
export const fetchLegalSources = createAsyncThunk(
  "leaveCompliance/fetchLegalSources",
  async (_, { rejectWithValue }) => {
    try {
      const res = await LeaveComplianceService.getLegalSources();
      return res.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to fetch legal sources");
    }
  }
);

export const createLegalSource = createAsyncThunk(
  "leaveCompliance/createLegalSource",
  async (payload: SaveLegalSourceDto, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveComplianceService.createLegalSource(payload);
      dispatch(fetchLegalSources());
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to create legal source");
    }
  }
);

export const updateLegalSource = createAsyncThunk(
  "leaveCompliance/updateLegalSource",
  async (payload: SaveLegalSourceDto, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveComplianceService.updateLegalSource(payload);
      dispatch(fetchLegalSources());
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update legal source");
    }
  }
);

export const deleteLegalSource = createAsyncThunk(
  "leaveCompliance/deleteLegalSource",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveComplianceService.deleteLegalSource(id);
      dispatch(fetchLegalSources());
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete legal source");
    }
  }
);

// Version History Thunk
export const fetchRuleVersionHistory = createAsyncThunk(
  "leaveCompliance/fetchVersionHistory",
  async (ruleId: number | undefined, { rejectWithValue }) => {
    try {
      const res = await LeaveComplianceService.getRuleVersionHistory(ruleId);
      return res.data || [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to fetch version history");
    }
  }
);

const leaveComplianceSlice = createSlice({
  name: "leaveCompliance",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Profiles
      .addCase(fetchComplianceProfiles.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchComplianceProfiles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profiles = action.payload;
      })
      .addCase(fetchComplianceProfiles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Statutory Rules
      .addCase(fetchStatutoryRules.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchStatutoryRules.fulfilled, (state, action) => {
        state.isLoading = false;
        state.statutoryRules = action.payload;
      })
      .addCase(fetchStatutoryRules.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Legal Sources
      .addCase(fetchLegalSources.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchLegalSources.fulfilled, (state, action) => {
        state.isLoading = false;
        state.legalSources = action.payload;
      })
      .addCase(fetchLegalSources.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Version History
      .addCase(fetchRuleVersionHistory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRuleVersionHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.versionHistory = action.payload;
      })
      .addCase(fetchRuleVersionHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Saving states
      .addMatcher(
        (action) =>
          action.type.endsWith("/pending") &&
          (action.type.includes("create") || action.type.includes("update")),
        (state) => {
          state.isSaving = true;
        }
      )
      .addMatcher(
        (action) =>
          (action.type.endsWith("/fulfilled") || action.type.endsWith("/rejected")) &&
          (action.type.includes("create") || action.type.includes("update")),
        (state) => {
          state.isSaving = false;
        }
      );
  },
});

export default leaveComplianceSlice.reducer;
